'use server'

import { revalidatePath } from 'next/cache'
import { redirect } from 'next/navigation'
import { headers } from 'next/headers'
import {
    createAuthenticationApplication,
} from '@/composition/server'
import { AuthenticationOperationError } from '@/contexts/identity/application/auth-session.port'
import {
    clientIP,
    rateLimitErrorCode,
    rateLimitSubject,
    takeRateLimit,
} from '@/lib/rate-limit'
import { credentialsSchema, oauthProviderSchema, signupSchema } from '@/lib/validation'
import {
    authRedirectURL,
    hasSupabasePublicConfig,
    isOAuthProviderEnabled,
} from '@/lib/auth-config'
import { safeAuthReturnPath } from '@/lib/auth-policy'
import {
    authenticationReturnPath,
    isContractorRegistrationPath,
} from './auth-intent'
import { completeAuthentication } from './complete-authentication'
import { storeContractorRegistrationIntent } from './contractor-registration-intent'
import { clearRecoveryGrant } from './recovery-grant'
import { hasAgreedToTerms, TERMS_AGREEMENT_FIELD, TERMS_AGREEMENT_VERSION } from './terms-agreement'
import { storeTermsAgreementIntent } from './terms-agreement-intent'

async function enforceAuthRateLimit(action: 'login' | 'signup' | 'oauth') {
    const requestHeaders = await headers()
    const limit = action === 'login' ? 10 : action === 'signup' ? 5 : 20
    const rate = await takeRateLimit(`auth:${action}:${clientIP(requestHeaders)}`, limit, 10 * 60_000)
    if (!rate.allowed) {
        const path = action === 'signup' ? '/auth/signup' : '/auth/login'
        redirect(`${path}?error=${rateLimitErrorCode(rate)}`)
    }
}

async function enforceIdentityRateLimit(action: 'login' | 'signup', email: string) {
    const limit = action === 'login' ? 10 : 3
    const window = action === 'login' ? 10 * 60_000 : 60 * 60_000
    const rate = await takeRateLimit(
        `auth:${action}:identity:${rateLimitSubject(email)}`,
        limit,
        window,
    )
    if (!rate.allowed) {
        const path = action === 'login' ? '/auth/login' : '/auth/signup'
        redirect(`${path}?error=${rateLimitErrorCode(rate)}`)
    }
}

export async function login(formData: FormData) {
    if (!hasSupabasePublicConfig()) {
        redirect('/auth/login?error=missing_config')
    }

    await enforceAuthRateLimit('login')

    const parsed = credentialsSchema.safeParse({
        email: formData.get('email') as string,
        password: formData.get('password') as string,
    })
    if (!parsed.success) redirect('/auth/login?error=invalid_credentials')
    await enforceIdentityRateLimit('login', parsed.data.email)

    const authentication = createAuthenticationApplication()
    let destination: string
    try {
        const signedIn = await authentication.signIn(parsed.data)
        destination = await completeAuthentication(signedIn, safeAuthReturnPath(formData.get('next') as string | null))
    } catch {
        await authentication.signOut().catch(() => undefined)
        redirect('/auth/login?error=invalid_credentials')
    }

    revalidatePath(destination, 'layout')
    redirect(destination)
}

export async function signup(formData: FormData) {
    if (!hasSupabasePublicConfig()) {
        redirect('/auth/signup?error=missing_config')
    }

    await enforceAuthRateLimit('signup')

    const requestedRole = formData.get('role')
    const next = authenticationReturnPath(formData.get('next'), requestedRole)
    const role = isContractorRegistrationPath(next) ? 'company' : 'customer'
    if (!hasAgreedToTerms(formData)) redirect(signupErrorPath('terms_required', next))
    const parsed = signupSchema.safeParse({
        email: formData.get('email'),
        password: formData.get('password'),
        role,
    })
    if (!parsed.success) redirect(signupErrorPath('invalid_signup', next))
    const { email, password } = parsed.data
    await enforceIdentityRateLimit('signup', email)
    try {
        await createAuthenticationApplication().signUp({
            email,
            password,
            requestedRole: role === 'company' ? 'contractor' : 'customer',
            emailRedirectTo: authRedirectURL('/auth/confirm', next),
            termsAgreementVersion: TERMS_AGREEMENT_VERSION,
        })
    } catch (error) {
        if (
            error instanceof AuthenticationOperationError &&
            error.failure === 'confirmation_email_cooldown'
        ) {
            redirect('/auth/login?message=check_email')
        }
        if (
            error instanceof AuthenticationOperationError &&
            error.failure === 'email_delivery_unavailable'
        ) {
            redirect(signupErrorPath('email_delivery_unavailable', next))
        }
        redirect(signupErrorPath('signup_failed', next))
    }

    revalidatePath('/', 'layout')
    redirect('/auth/login?message=check_email')
}

export async function startOAuth(formData: FormData) {
    if (!hasSupabasePublicConfig()) redirect('/auth/login?error=missing_config')
    await enforceAuthRateLimit('oauth')

    const provider = oauthProviderSchema.safeParse(formData.get('provider'))
    let providerEnabled = false
    if (provider.success) {
        try {
            providerEnabled = isOAuthProviderEnabled(provider.data)
        } catch {
            providerEnabled = false
        }
    }
    if (!provider.success || !providerEnabled) {
        redirect('/auth/login?error=oauth_provider_unavailable')
    }
    const next = authenticationReturnPath(formData.get('next'), formData.get('role'))
    // Only the signup screen sends the agreement field; returning users sign in from the login screen.
    if (formData.has(TERMS_AGREEMENT_FIELD) && !hasAgreedToTerms(formData)) {
        redirect(signupErrorPath('terms_required', next))
    }

    let authorizationURL: string
    try {
        authorizationURL = await createAuthenticationApplication().startOAuth({
            provider: provider.data,
            redirectTo: authRedirectURL('/auth/callback', next),
        })
    } catch {
        redirect('/auth/login?error=oauth_start_failed')
    }
    await storeContractorRegistrationIntent(isContractorRegistrationPath(next))
    if (formData.has(TERMS_AGREEMENT_FIELD)) await storeTermsAgreementIntent()
    redirect(authorizationURL)
}

// Contractor registrations must return to the contractor screen, the only one with the email form.
function signupErrorPath(error: string, next: string) {
    const query = new URLSearchParams({ error })
    if (isContractorRegistrationPath(next)) {
        query.set('role', 'company')
        query.set('next', next)
    }
    return `/auth/signup?${query}`
}

export async function signOut() {
    if (!hasSupabasePublicConfig()) {
        redirect('/')
    }

    await createAuthenticationApplication().signOut().catch(() => undefined)
    await clearRecoveryGrant()
    revalidatePath('/', 'layout')
    redirect('/')
}
